import { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-location';
import { Permission } from '@weave/schema-gen-ts/dist/shared/waccess';
import { Chart, TooltipInterfaceProps } from '@frontend/charts';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useScopedAppFlagStore } from '@frontend/scope';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Chip } from '@frontend/design-system';
import { URLs } from '../../../../constants';
import { featureFlags } from '../../../../feature-flags';
import { useHasAclAccess } from '../../../../hooks';
import { trackingIds } from '../../../../tracking-ids';
import { CompareLocationsButton } from '../../../compare-locations-button';
import { DemoChip } from '../../../demo-chip';
import { dashboardChartStyle } from '../../../homepage-analytics/styles';
import { useFetchNSRROITimeline } from '../hooks';

const colors = {
  existingPatients: theme.colors.primary30,
  newPatients: theme.colors.warning50,
  practice: theme.colors.secondary.seaweed40,
};

const labels = {
  existingPatients: i18next.t('Existing Patients', { ns: 'analytics' }),
  newPatients: i18next.t('New Patients', { ns: 'analytics' }),
  practice: i18next.t('No-Show Rate', { ns: 'analytics' }),
};

interface Props extends ReturnType<typeof useFetchNSRROITimeline> {
  afterChart?: ReactNode;
  beforeChart?: ReactNode;
  bottomContentRenderer?: ReactNode;
  infoTip: ReactNode;
  isOnDashboard?: boolean;
  legendValues?: Record<string, number | string>;
  onShowMoreClick?: () => void;
  showCompareMulti?: boolean;
  showLegends?: boolean;
  title: string;
  tooltipBottomContentRenderer?: TooltipInterfaceProps['bottomContentRenderer'];
  trackingIdBase: string;
}

export const NSRRoiTimelineChartBase = ({
  afterChart,
  beforeChart,
  bottomContentRenderer,
  chartData,
  infoTip,
  isLoading,
  isOnDashboard,
  legendValues,
  onShowMoreClick,
  showCompareMulti,
  showLegends,
  title,
  tooltipBottomContentRenderer,
  trackingIdBase,
}: Props) => {
  const { t } = useTranslation('analytics');
  const { getFeatureFlagValue } = useScopedAppFlagStore();
  const isDemoAccount = getFeatureFlagValue(featureFlags.noShowRateRoiDemoData);
  const hasAclAccess = useHasAclAccess([Permission.ANALYTICS_READ]);
  const navigate = useNavigate();

  return (
    <Chart
      bottomContentRenderer={bottomContentRenderer}
      colors={colors}
      css={isOnDashboard ? dashboardChartStyle : undefined}
      isLoading={isLoading}
      labels={labels}
      onClick={onShowMoreClick}
      style={isOnDashboard ? {} : { marginBottom: 0 }}
      trackingId={`${trackingIdBase}-chart`}
    >
      <Chart.Header
        actions={
          isOnDashboard
            ? [
                {
                  label: t('Show More'),
                  omit: !hasAclAccess,
                  onClick: () => {
                    navigate({
                      to: URLs.ROI_NO_SHOW_RATE,
                    });
                  },
                  trackingId: trackingIds.analytics.showMoreNoShowRateRoi,
                },
              ]
            : []
        }
        title={title}
        infoTip={isOnDashboard ? undefined : infoTip}
        bottomElement={
          showCompareMulti && (
            <CompareLocationsButton onClick={onShowMoreClick} trackingId={`${trackingIdBase}-compare-locations`} />
          )
        }
        leftElement={isDemoAccount && <DemoChip />}
        removeHeaderBottomMargin={!showCompareMulti}
      />
      {beforeChart}
      <Chart.AreaChart
        appearance={{
          customTooltipTitle: ({ groupName }) => {
            return (
              <Chip leftElement={<Icon name='calendar-small' />} variant='neutral'>
                {groupName}
              </Chip>
            );
          },
          customYAxisTickFormat: formatters.appendPercent,
          height: 200,
          margin: { top: 30 },
          showXAxis: true,
          showYAxis: true,
          tooltipBottomContentRenderer,
          xAxisTickWidth: 100,
        }}
        data={chartData}
        formatValue={formatters.appendPercent}
      />
      {showLegends && <Chart.Legends style={{ marginTop: theme.spacing(2) }} values={legendValues} />}
      {afterChart}
    </Chart>
  );
};
